import React, { useMemo, useState, useEffect, useContext } from 'react';

import { AiFillInfoCircle } from 'react-icons/ai';
import { NotificationManager } from 'react-notifications';
import { useNavigate } from 'react-router-dom';

import moment from 'moment';

/** components */
import { RenderMainContent } from './helpersForReviewContent/RenderMainContent';
import { SetFeatures } from './helpersForReviewContent/SetFeatures';

import ReviewLicenseSection from './ReviewLicenseSection';

/** images */
import editImg from '../../assets/images/edit.png';

/** images */
import TenantConfig from '../../config';

/** constants */
import { doesApplicationHaveWIPSections, isApplicationWIP } from '../../constants/status';

/** context */
import { UserContext } from '../../context/UserContext';
import checkUpdateTimeDifference from '../../helpers/checkUpdateTimeDifference';

/** helpers */
import { capitalizeFirstLetter, sanitizeHtmlNoLink, sanitizeHtml } from '../../helpers/format';
import { useRudder } from '../../hooks/rudder';
import { useForm } from '../../hooks/useForm';

/** service */
import { updateApplication, validateProperty, getApplication } from '../../service/api';
import { Transition } from '../PageElements';

const Review = () => {
  const navigate = useNavigate();
  const { getPropertyFields } = useForm();
  const { appData, updateAppData, profile, intervalObj, setIntervalObj } = useContext(UserContext);
  const [consentFeatures, setConsentFeatures] = useState();
  const [features, setFeatures] = useState();
  const [isMainApplicant, setIsMainApplicant] = useState(true);
  const [isInitialSubmission, setIsInitialSubmission] = useState(false);
  const [lastName, setLastName] = useState();
  const [firstName, setFirstName] = useState();
  const [sections, setSections] = useState();
  const { errorEvent } = useRudder();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalConsent, setModalConsent] = useState(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);
  const [submitted, setIsSubmitted] = useState(false);
  const [properties, setProperties] = useState(appData?.propertySequence);
  const [isLong, setIsLong] = useState(false);
  const [cycleTime, setCycleTime] = useState(10000); // 10 seconds - default cycle during first 2 mins
  const [showVideoModal, setShowVideoModal] = useState(false);
  const regExpForInput =
    /[^a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/gu;
  const replacer = (str) => str.replaceAll(regExpForInput, '');
  const handlerFirstNameChange = (e) => setFirstName(replacer(e.target.value));
  const handlerLastNameChange = (e) => setLastName(replacer(e.target.value));

  useEffect(() => {
    SetFeatures({
      appData,
      setFeatures,
      setConsentFeatures,
      setProperties,
      setSections,
      setIsMainApplicant,
      setIsInitialSubmission,
    });
  }, [appData]);

  useEffect(async () => {
    if (!Object.keys(appData).length) {
      try {
        await getApplication();
      } catch (e) {
        navigate('/dashboard');
      }
    }
    return () => {
      clearInterval(intervalObj);
    };
    // eslint-disable-next-line
  }, []);

  const plaidFeature = features?.find((feature) => feature.itemType === 'PlaidTokenProperty');

  const applicantFeature = features?.find((feature) => feature.itemType === 'ApplicantProperty');

  const [termChecks, setTermChecks] = useState({});

  const applicationEditable =
    isApplicationWIP(appData.status) || doesApplicationHaveWIPSections(appData);
  const handleTermChecks = (feature) => {
    setTermChecks((prevState) => {
      const newState = { ...prevState };
      if (newState[feature.name]) {
        delete newState[feature.name];
      } else {
        newState[feature.name] = [true, feature.required];
      }

      return newState;
    });

    setModalIsOpen(true);
    setModalConsent(feature);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const goToEditOwnerPage = (sectionIndex, propertyIndex, index) => {
    navigate(
      `/product/${appData.loanProductId}/application/${appData.id}/?edit=1&sectionIndex=${sectionIndex}&propertyIndex=${propertyIndex}&ownerIndex=${index}`
    );
  };

  const goToEdit = (sectionIndex, propertyIndex) => {
    navigate(
      `/product/${appData.loanProductId}/application/${appData.id}/?edit=1&sectionIndex=${sectionIndex}&propertyIndex=${propertyIndex}`
    );
  };

  const handleSaveComeAndComeBack = () => {
    navigate(TenantConfig?.review.saveComebackUrl);
  };

  const handleSubmit = async () => {
    const consentFeature = consentFeatures.find(
      (feature) => feature.name.toLowerCase() === 'consent'
    );

    const consentValue =
      consentFeature.itemType === 'SimpleProperty'
        ? true
        : consentFeature.itemType === 'ConsentProperty'
        ? [{ consented: true, userId: profile.id }]
        : { consent: true };
    setIsSubmitted(true);
    window.scrollTo(0, 0);
    try {
      const updatedData = await updateApplication([
        {
          itemType: consentFeature.itemType,
          dataType: consentFeature.dataType,
          name: consentFeature.name,
          value: consentValue,
        },
      ]);
      if (updatedData && updatedData.data) {
        const res = await validateProperty();

        if (res.data && res.data.errors) {
          let resIsLong = checkUpdateTimeDifference(res.data.tlai.updateDate);
          if (resIsLong) {
            setCycleTime(60000); // set 1 min cycle during next 30 mins wait
            setIsLong(true);
          }
          if (!res.data.errors.length) {
            let intervalRef = setInterval(async () => {
              try {
                console.log('reloadAppData call');
                const res1 = await getApplication();
                if (res1 && res1.data) {
                  updateAppData(res1.data);
                }
              } catch (error) {
                clearInterval(intervalObj);
                NotificationManager.warning('There was an error retrieving your application.');
                navigate('/dashboard');
                console.log(error);
              }
            }, cycleTime);
            setIntervalObj(intervalRef);
          } else {
            clearInterval(intervalObj);
            errorEvent('Error Validate', res.data.errors);
            setFieldErrors(res.data.errors);
            setIsSubmitted(false);
            const fileProperty = features.find((feature) => feature.itemType === 'FileProperty');
            const missingFiles =
              fileProperty && fileProperty.name
                ? res.data.errors.filter((error) => {
                    return error.property.includes(fileProperty?.name);
                  })
                : [];

            const applicationError = res.data.errors.find((error) => {
              return !error.property.includes('.');
            });

            const validatationErrors = res.data.errors.find((error) => {
              return error.property.includes('.');
            });
            if (applicationError) {
              setError(
                TenantConfig?.review.applicationErrorMesage !== 'default'
                  ? TenantConfig?.review.applicationErrorMesage
                  : applicationError.message
              );
            } else if (missingFiles && missingFiles.length > 0) {
              setError(
                'This application is not finished. To complete the application please upload all required documents in an acceptable format. <br />If you need more time please click "Save and come Back Later"'
              );
            } else if (validatationErrors) {
              const presubmissionCodeError = res.data.errors.find((error) => {
                return error.property.includes('preSubmissionOfferCode');
              });
              if (presubmissionCodeError) {
                setError(
                  TenantConfig?.review.presubmissionOfferCodeError
                    ? TenantConfig?.review.presubmissionOfferCodeError
                    : applicationError.message
                );
              } else {
                setError(
                  'It looks like some sections/fields were not completed or some field values are incorrect. Please see the sections & fields highlighted in red, you need to correct all issues before you can submit.'
                );
              }
            } else {
              errorEvent('Error Validate', res.data.errors);
              setIsSubmitted(false);
              setError(TenantConfig?.review.finalErrorMsg);
              NotificationManager.warning(
                "We couldn't submit your application please check the error message below."
              );
            }
          }
        }
      }
    } catch (error) {
      setIsSubmitted(false);
      NotificationManager.warning(
        'An error occurred when saving your application please check the error message below.'
      );
      errorEvent('Error API', error);
      const errors = error?.response?.data?.errors;
      if (errors?.length) {
        setError(errors[0].message);
      }
      console.log(errors);
    }
  };

  const formatCurrency = (value) => {
    if (value) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });
      return formatter.format(value);
    } else {
      return 'N/A';
    }
  };

  const getPropertyValue = (property, name) => {
    const feature = features.find((feature) => feature.name === property);
    const fields = getPropertyFields(feature, feature.value);
    const dataFormatting = fields.reduce((a, b) => ({ ...a, [b.name]: b }), {});
    if (name == 'address') {
      return feature.value['address'] ? feature.value['address'].deliveryLine1 : '';
    } else if (dataFormatting[name].type == 'checkbox') {
      return feature.value[name] ? 'Yes' : 'No';
    } else if (dataFormatting[name].type == 'dollarInput') {
      return formatCurrency(feature.value[name]);
    } else if (dataFormatting[name].type === 'date') {
      return moment(feature.value[name]).format('MM-DD-YYYY');
    } else if (dataFormatting[name].type === 'selectOne') {
      const options = dataFormatting[name].options;
      const selectedOption = options.find(
        (option) => option.value == feature.value[name]?.toString()
      );
      return selectedOption?.label;
    } else if (dataFormatting[name].type === 'selectMultiple') {
      return feature.value[name]?.toString().replaceAll(',', ', ');
    }
    return feature.value[name]?.toString();
  };

  const getFileValue = (property, name) => {
    const feature = features.find((feature) => feature.name === property);
    const file = feature.value.find((file) => file.fileName === name || file.fileType === name);
    if (file) {
      return file.status;
    } else {
      return 'REQUESTED';
    }
  };

  const getApplicantPropertyValue = (index, name) => {
    const feature = features.find((feature) => feature.itemType === 'ApplicantProperty');
    const fields = getPropertyFields(feature, feature.value[index]);
    const dataFormatting = fields.reduce((a, b) => ({ ...a, [b.name]: b }), {});

    if (name === 'address') {
      return feature.value[index]['address'] ? feature.value[index]['address'].deliveryLine1 : '';
    } else if (dataFormatting[name].type === 'checkbox') {
      return feature.value[index][name] ? 'Yes' : 'No';
    } else if (dataFormatting[name].type == 'dollarInput') {
      return formatCurrency(feature.value[index][name]);
    } else if (dataFormatting[name].type === 'date') {
      return moment(feature.value[index][name]).format('MM-DD-YYYY');
    } else if (dataFormatting[name].type === 'selectOne') {
      const options = dataFormatting[name].options;
      const selectedOption = options.find(
        (option) => option.value == feature.value[index][name]?.toString()
      );
      return selectedOption?.label;
    }
    return feature.value[index][name]?.toString();
  };

  const isSubmitButtonDisabled = useMemo(() => {
    const requiredFeatures = consentFeatures?.filter((f) => f.required);
    //we check if there is a consentProperty here.
    //If we check if the e-signature is here
    const containsESignature = !!consentFeatures?.find((f) => f.itemType === 'ConsentProperty');
    const isEsigned = containsESignature && isInitialSubmission ? !!lastName && !!firstName : true;
    const requiredChecked = Object.keys(termChecks).filter((k) => termChecks[k][1]);
    return requiredFeatures
      ? requiredFeatures.length !== requiredChecked.length || !isEsigned
      : false;
  }, [consentFeatures, termChecks, firstName, lastName]);

  const renderConsentCheckbox = (consentFeature) => {
    if (consentFeature.itemType === 'ConsentProperty') {
      return (
        <div className="terms-checks" key={`checkbox_${consentFeature.name}`}>
          <div className="CustomCheckBox" style={{ textAlign: 'left' }}>
            <label htmlFor={`terms-checks-${consentFeature.name}`}>
              <input
                type="checkbox"
                value={termChecks[consentFeature.name]}
                id={`terms-checks-${consentFeature.name}`}
                name="terms-checks"
                onChange={() => handleTermChecks(consentFeature)}
              />
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    consentFeature.conditionalFormats['consented'][0].format.label
                  ),
                }}
                onClick={() => {
                  setModalIsOpen(true);
                  setModalConsent(consentFeature);
                }}
              />
            </label>
          </div>
          {!!isInitialSubmission && (
            <div className="subSection" key={'subSection'}>
              <div className="subSectionTitle">E-signature</div>
              <div className="form-list" style={{ marginBottom: '15px' }}>
                <label htmlFor="Estimated Down-Payment">
                  Legal First Name<span className="required">*</span>
                  <span className="form-icon" data-tip="Type for consent">
                    <AiFillInfoCircle />
                  </span>
                </label>
                <input
                  id="firstNameInput"
                  name="firstName"
                  required
                  type="text"
                  placeholder="Type for consent"
                  autoComplete="off"
                  list="autocompleteOff"
                  value={firstName}
                  onChange={handlerFirstNameChange}
                  onPaste={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                  onCopy={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                  spellCheck={false}
                />
              </div>
              <div className="form-list" style={{ marginBottom: '15px' }}>
                <label htmlFor="Estimated Down-Payment" required>
                  Legal Last Name<span className="required">*</span>
                  <span className="form-icon" data-tip="Type for consent">
                    <AiFillInfoCircle />
                  </span>
                </label>
                <input
                  id="lastNameInput"
                  name="lastName"
                  type="text"
                  placeholder="Type for consent"
                  autoComplete="off"
                  list="autocompleteOff"
                  value={lastName}
                  onChange={handlerLastNameChange}
                  onPaste={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                  onCopy={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                  spellCheck={false}
                />
              </div>
            </div>
          )}
        </div>
      );
    } else {
      return (
        <div className="terms-checks" key={`checkbox_${consentFeature.name}`}>
          <div className="CustomCheckBox">
            <label htmlFor={`terms-checks-${consentFeature.name}`}>
              <input
                type="checkbox"
                checked={
                  termChecks[consentFeature.name] ? termChecks[consentFeature.name][0] : false
                }
                value={termChecks[consentFeature.name]}
                id={`terms-checks-${consentFeature.name}`}
                name="terms-checks"
                onChange={() => handleTermChecks(consentFeature)}
              />
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    consentFeature.itemType === 'SimpleProperty'
                      ? consentFeature.format.label
                      : consentFeature.itemType === 'ConsentProperty'
                      ? consentFeature.conditionalFormats['consented'][0].format.label
                      : consentFeature.format.terms.label
                  ),
                }}
                onClick={() => {
                  setModalIsOpen(true);
                  setModalConsent(consentFeature);
                }}
              />
            </label>
          </div>
        </div>
      );
    }
  };
  const renderOwnerSectionTitle = (applicantFeature, owner) => {
    let title = TenantConfig.review.ownersectionCustomTitle
      ? TenantConfig.review.ownersectionCustomTitle
      : capitalizeFirstLetter(applicantFeature.section);
    title = title + ' - ' + owner.firstName + ' ' + owner.lastName;
    return title;
  };

  const renderPropertyArea = (property, propertyIndex, sectionIndex) => {
    const feature = features.find((feature) => feature.name === property);

    const dataType = feature.dataType;

    if (dataType === 'applicant') {
      let owners = [];
      let ownerIndex;
      if (!isMainApplicant) {
        owners = applicantFeature.value.filter((owner, idx) => {
          if (owner.firstName == profile.firstName && owner.lastName == profile.lastName) {
            ownerIndex = idx;
            return true;
          }
        });
      } else {
        owners = owners = applicantFeature.value;
      }
      return (
        applicantFeature &&
        applicantFeature.value &&
        owners.map((owner, index) => {
          if (owner.firstName) {
            const fields = getPropertyFields(applicantFeature, owner);
            return (
              <div key={'applicant' + index}>
                <div className="item-detail general-detail">
                  <h3>{renderOwnerSectionTitle(applicantFeature, owner)}</h3>

                  <div className="detail-grid">
                    {fields
                      .sort((field1, field2) => field1.order - field2.order)
                      .map((field) => {
                        const sectionFieldPrefix = capitalizeFirstLetter(
                          field?.label?.split(' ')[0]
                        );
                        if (field.dependsOn) {
                          if (Array.isArray(owner[field.dependsOn])) {
                            if (!owner[field.dependsOn].includes(field.condition)) return;
                          } else {
                            if (field.condition.toString() != owner[field.dependsOn]?.toString())
                              return;
                          }
                        }
                        if (field.type === 'hidden') return;
                        if (['address'].includes(field.name)) {
                          return (
                            <React.Fragment key={'applicant' + index + 'detail-item' + field.name}>
                              <div className="detail-item">
                                <h6>{fields.find((f) => f.name === field.name).label}</h6>
                                <p>{owner?.address?.deliveryLine1}</p>
                              </div>
                              <div className="detail-item">
                                <h6>
                                  {fields
                                    .find((f) => f.name === field.name)
                                    .label.replace('Line 1', 'Line 2')}
                                </h6>
                                <p>{owner?.address?.deliveryLine2}</p>
                              </div>
                              <div className="detail-item">
                                <h6>{sectionFieldPrefix} City</h6>
                                <p>{owner?.address?.components?.cityName}</p>
                              </div>
                              <div className="detail-item">
                                <h6>{sectionFieldPrefix} State</h6>
                                <p>{owner?.address?.components?.state}</p>
                              </div>
                              <div className="detail-item">
                                <h6>{sectionFieldPrefix} Zip Code</h6>
                                <p>{owner?.address?.components?.zipCode}</p>
                              </div>
                            </React.Fragment>
                          );
                        }
                        return (
                          <div
                            className={`detail-item ${field.style ? field.style : ''}`}
                            key={'applicant' + index + 'detail-item' + field.name}
                          >
                            <h6
                              dangerouslySetInnerHTML={{
                                __html: sanitizeHtmlNoLink(field.label),
                              }}
                            />
                            <p>
                              {getApplicantPropertyValue(
                                isMainApplicant ? index : ownerIndex,
                                field.name
                              )}
                            </p>
                          </div>
                        );
                      })}
                  </div>
                </div>

                <div
                  className="edit-action d-flex"
                  onClick={() => goToEditOwnerPage(sectionIndex, propertyIndex, index)}
                >
                  {applicationEditable && (
                    <>
                      <img src={editImg} alt="" />
                      <span>I have some edits!</span>
                    </>
                  )}
                </div>
              </div>
            );
          }
        })
      );
    } else if (dataType === 'plaidtoken') {
      const plaidError = fieldErrors.find((fieldError) =>
        fieldError.property.startsWith('plaidToken')
      );

      return (
        plaidFeature &&
        plaidFeature.value &&
        plaidFeature.value?.plaidLinkage?.plaidNotLinkable == false && (
          <div key={'property' + propertyIndex}>
            <div className="item-detail financial-detail">
              <h3>{capitalizeFirstLetter(feature.section)}</h3>
              <div className={plaidError ? 'error-item detail-item' : 'detail-item'}>
                <h6>
                  {
                    plaidFeature.value.financialInstruments[
                      plaidFeature.value.selectedFinancialInstrumentIndex
                    ]?.bankAccountType
                  }{' '}
                  Account
                </h6>
                <p>
                  {
                    plaidFeature.value.financialInstruments[
                      plaidFeature.value.selectedFinancialInstrumentIndex
                    ]?.associatedBankName
                  }
                  &nbsp;#
                  {
                    plaidFeature.value.financialInstruments[
                      plaidFeature.value.selectedFinancialInstrumentIndex
                    ]?.maskedAccountNumber
                  }
                </p>

                {plaidError ? <p className="error-message">{plaidError.message}</p> : ''}
              </div>
            </div>

            <div
              className="edit-action d-flex"
              onClick={() => goToEdit(sectionIndex, propertyIndex)}
            >
              <img src={editImg} alt="" />
              <span>I have some edits!</span>
            </div>
          </div>
        )
      );
    } else if (dataType === 'financialinstrument') {
      return (
        feature &&
        feature.value &&
        feature.value?.maskedAccountNumber &&
        plaidFeature &&
        plaidFeature.value &&
        plaidFeature.value?.plaidLinkage?.plaidNotLinkable == true && (
          <div key={'property' + propertyIndex}>
            <div className="item-detail financial-detail">
              <h3>{capitalizeFirstLetter(feature.section)}</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <h6>Account holder Name</h6>
                  <p>{feature.value.associatedCustomerName}</p>
                </div>
                <div className="detail-item">
                  <h6>Account Number</h6>
                  <p>#{feature.value.maskedAccountNumber}</p>
                </div>
                <div className="detail-item">
                  <h6>Routing Number</h6>
                  <p>#{feature.value.maskedRoutingNumber}</p>
                </div>
                <div className="detail-item">
                  <h6>Bank Account Type</h6>
                  <p>{feature.value.bankAccountType}</p>
                </div>
              </div>
            </div>

            <div
              className="edit-action d-flex"
              onClick={() => goToEdit(sectionIndex, propertyIndex)}
            >
              <img src={editImg} alt="" />
              <span>I have some edits!</span>
            </div>
          </div>
        )
      );
    } else if (dataType === 'file') {
      return (
        feature &&
        feature.value && (
          <div key={'property' + propertyIndex}>
            <div className="item-detail general-detail">
              <h3>Uploaded Docs</h3>
              <div className="detail-grid">
                {getPropertyFields(feature, feature.value)
                  .sort((field1, field2) => field1.order - field2.order)
                  .filter((field) => !field.name.includes('bankVerificationStatement'))
                  .map((field) => {
                    return (
                      <div
                        className="detail-item"
                        key={'property' + propertyIndex + 'detail-item' + field.name}
                      >
                        <h6
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtmlNoLink(field.label),
                          }}
                        />
                        <p>{getFileValue(property, field.name)}</p>
                      </div>
                    );
                  })}
              </div>
            </div>
            <div
              className="edit-action d-flex"
              onClick={() => goToEdit(sectionIndex, propertyIndex)}
            >
              <img src={editImg} alt="" />
              <span>I have some edits!</span>
            </div>
          </div>
        )
      );
    } else if (dataType === 'idVerify') {
      return (
        <div key={'property' + propertyIndex}>
          <div className="item-detail general-detail">
            <h3>ID Verification</h3>
            <div className="detail-grid">
              {feature?.value?.length > 0 && feature.value[0].status
                ? feature.value[0].status
                : 'Incomplete'}
            </div>
          </div>
          <div className="edit-action d-flex" onClick={() => goToEdit(sectionIndex, propertyIndex)}>
            <img src={editImg} alt="" />
            <span>I have some edits!</span>
          </div>
        </div>
      );
    } else if (dataType === 'license') {
      return (
        <ReviewLicenseSection
          propertyIndex={propertyIndex}
          feature={feature}
          goToEdit={goToEdit}
          sectionIndex={sectionIndex}
          editImg={editImg}
          applicationEditable={applicationEditable}
          capitalizeFirstLetter={capitalizeFirstLetter}
        />
      );
    }
    return (
      feature &&
      feature.value && (
        <div key={'property' + propertyIndex}>
          <div className="item-detail general-detail">
            <h3>{capitalizeFirstLetter(feature.section)}</h3>
            <div className="detail-grid">
              {getPropertyFields(feature, feature.value)
                .sort((field1, field2) => field1.order - field2.order)
                .map((field) => {
                  const sectionFieldPrefix = capitalizeFirstLetter(field?.label?.split(' ')[0]);
                  if (field.dependsOn) {
                    if (Array.isArray(feature.value[field.dependsOn])) {
                      if (!feature.value[field.dependsOn].includes(field.condition)) return;
                    } else {
                      if (field.condition.toString() != feature.value[field.dependsOn]?.toString())
                        return '';
                    }
                  }
                  if (field.type === 'hidden') return;
                  if (['address'].includes(field.name)) {
                    return (
                      <React.Fragment key={'property' + propertyIndex + 'detail-item' + field.name}>
                        <div className="detail-item">
                          <h6>{feature.format['address'].label}</h6>
                          <p>{feature.value.address?.deliveryLine1}</p>
                        </div>
                        <div className="detail-item">
                          <h6>{feature.format['address'].label.replace('Line 1', 'Line 2')}</h6>
                          <p>{feature.value.address?.deliveryLine2}</p>
                        </div>
                        <div className="detail-item">
                          <h6>{sectionFieldPrefix} City</h6>
                          <p>{feature.value.address?.components?.cityName}</p>
                        </div>
                        <div className="detail-item">
                          <h6>{sectionFieldPrefix} State</h6>
                          <p>{feature.value.address?.components?.state}</p>
                        </div>
                        <div className="detail-item">
                          <h6>{sectionFieldPrefix} Zip Code</h6>
                          <p>{feature.value.address?.components?.zipCode}</p>
                        </div>
                      </React.Fragment>
                    );
                  }

                  const errorField = fieldErrors.find(
                    (error) => error.property === property + '.' + field.name
                  );

                  return (
                    <div
                      className={`detail-item ${field.style ? field.style : ''} ${
                        errorField ? 'error-item' : ''
                      }`}
                      key={'property' + propertyIndex + 'detail-item' + field.name}
                    >
                      <h6
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtmlNoLink(field.label),
                        }}
                        className={errorField ? 'error-message' : ''}
                      />
                      <p>{getPropertyValue(property, field.name)}</p>
                      {errorField ? <p className="error-message">{errorField.message}</p> : ''}
                    </div>
                  );
                })}
            </div>
          </div>
          <div className="edit-action d-flex" onClick={() => goToEdit(sectionIndex, propertyIndex)}>
            {applicationEditable && (
              <>
                <img src={editImg} alt="" />
                <span>I have some edits!</span>
              </>
            )}
          </div>
        </div>
      )
    );
  };

  const handleChangeSectionIndex = (sectionIndex) => {
    if (sectionIndex < sections.length - 1) {
      navigate(`/application/${appData.id}/?edit=1&sectionIndex=${sectionIndex}&propertyIndex=0`);
    }
  };

  const preparingPropsForRenderMainContent = {
    error,
    properties,
    sections,
    handleChangeSectionIndex,
    setShowVideoModal,
    showVideoModal,
    features,
    renderPropertyArea,
    isInitialSubmission,
    applicationEditable,
    handleSaveComeAndComeBack,
    isSubmitButtonDisabled,
    handleSubmit,
    modalConsent,
    modalIsOpen,
    closeModal,
    consentFeatures,
    renderConsentCheckbox,
    navigateToDashboard: () => navigate('/dashboard'),
  };

  const renderSandbox = () => {
    return <Transition isLong={isLong} />;
  };

  return (
    <>
      {submitted || !consentFeatures || !features
        ? renderSandbox()
        : RenderMainContent(preparingPropsForRenderMainContent)}
    </>
  );
};

export default Review;
